import { ts } from '@shared/lib/diagTs';
import { cqg } from './proto/cqgQuotes.js';
import { liveQuotesStore } from './liveQuotesStore';

/**
 * Browser-side CQG quote decoder.
 *
 * The browser owns the WebSocket to CQG (see cqgRelay.ts). Historically every frame was an opaque
 * byte pipe to the BE, which parsed the Protobuf and echoed `quotes.update` back over SignalR. That
 * round-trip means live quotes die whenever the BE's in-memory session does (e.g. a BE restart:
 * "CqgInbound: no session ...; dropping N bytes"). This module decodes the quote-relevant parts of
 * each `ServerMsg` *in the browser* and pushes straight into `liveQuotesStore`, so the components
 * that read `useLiveQuote(symbol)` tick directly off the socket — independent of BE session state.
 *
 * It is purely additive: cqgRelay still forwards the raw bytes to the BE for orders/positions/
 * history. We only mirror the read path CQG already sends us.
 *
 * Mirrors the BE RealTimeMarketDataHandler exactly:
 *   - contractId → { symbol, scale } comes from SymbolResolutionReport.ContractMetadata.
 *   - real price = quote.scaledPrice * correctPriceScale (sint64 × double).
 *   - Quote.type 0=trade(last) / 1=bestbid / 2=bestask.
 */

interface ContractMeta {
  symbol: string;
  scale: number;      // correct_price_scale (multiplier applied to scaledPrice)
  tickSize?: number;
  tickValue?: number;
}

// contractId → metadata, accumulated from SymbolResolutionReports seen on the socket. RESET on each
// fresh CQG socket open (resetContractResolution, called by cqgRelay) — CQG reuses contractIds across
// sessions for DIFFERENT instruments, so a mapping learned in a prior session would mis-scale/mis-label
// a quote on a reused id (the ×100 phantom). CQG re-sends resolutions on every (re)connect, so the map
// repopulates for the current session.
const contractsById = new Map<number, ContractMeta>();

// Fired the first time a contract resolves (symbol ↔ contractId learned). The browser-side
// market-data subscriber (cqgQuoteSubscriber) listens so it can send a market_data_subscription the
// instant a wanted symbol resolves. Contracts only resolve *after* logon, so this is the natural
// "session ready for this symbol" signal.
type ResolutionListener = (symbol: string, contractId: number) => void;
const resolutionListeners = new Set<ResolutionListener>();

/** Subscribe to contract-resolution events (symbol ↔ contractId). Returns an unsubscribe. */
export function onContractResolved(listener: ResolutionListener): () => void {
  resolutionListeners.add(listener);
  return () => { resolutionListeners.delete(listener); };
}

type ResolutionFailureListener = (requestId: number, statusCode: number, text: string | null) => void;
const resolutionFailureListeners = new Set<ResolutionFailureListener>();

/** Subscribe to FAILED information requests (`status_code >= 100`) by request id. Returns unsubscribe. */
export function onResolutionFailed(listener: ResolutionFailureListener): () => void {
  resolutionFailureListeners.add(listener);
  return () => { resolutionFailureListeners.delete(listener); };
}

/** Reverse of contractsById: the CQG contractId currently mapped to a symbol, if resolved yet. */
export function getContractIdBySymbol(symbol: string): number | undefined {
  for (const [id, meta] of contractsById) if (meta.symbol === symbol) return id;
  return undefined;
}

/**
 * The current-session symbol for a CQG contractId, if it has resolved yet. The optimistic-fill
 * decoder uses this to label a live fill by symbol — and DEFERS (skips) the fill when it returns
 * undefined, exactly like quotes defer for an unmapped contract (CQG reuses contractIds across
 * sessions, so a fill on an unresolved id must wait for THIS session's ContractMetadata; the BE's
 * trades.changed → /positions refetch is the durable backstop).
 */
export function getSymbolByContractId(contractId: number): string | undefined {
  return contractsById.get(contractId)?.symbol;
}

/**
 * Drop all session-scoped contract resolution + cached quotes. The relay calls this when a fresh CQG
 * socket opens (a new session). CQG reuses contractIds across sessions for DIFFERENT instruments, so a
 * mapping learned in a prior session can mis-scale / mis-label a quote on a reused id — the ×100
 * phantom (a real MES quote applied under a stale symbol/scale). Clearing makes every contractId
 * UNRESOLVED until THIS session re-sends its ContractMetadata, so quotes DEFER (a transient blank)
 * rather than being applied with a stale scale/symbol. Mirrors the BE's StartNewSession generation
 * bump + ILastPriceCache.Clear on logon.
 */
export function resetContractResolution(): void {
  contractsById.clear();
  quotedOnce.clear();
  liveQuotesStore.clear();
}

const TYPE_TRADE = 0;
const TYPE_BESTBID = 1;
const TYPE_BESTASK = 2;

/** Symbols we've already emitted a "first quote applied" diagnostic for (so the log isn't spammy). */
const quotedOnce = new Set<string>();

/** What a single decoded frame did — returned so the relay can surface it in the BE log for diagnosis. */
export interface DecodeOutcome {
  /** Newly-learned contractId→symbol resolutions this frame (rare; one per contract per session). */
  newResolutions: string[];
  /** Symbols whose FIRST live quote was applied this frame (rare; one per symbol). */
  firstQuotes: string[];
  /** Total quote batches applied this frame (for throughput sanity). */
  quotesApplied: number;
  /** ContractIds that streamed quotes we couldn't map yet (no resolution seen) — the "not resolving" signal. */
  unmappedContractIds: number[];
}

/**
 * Decode one ServerMsg frame (raw bytes from the CQG socket) and apply any quotes/contract
 * metadata it carries. Never throws — a malformed frame is logged once and skipped so the relay
 * keeps forwarding to the BE regardless. Returns a small outcome the caller can log for diagnosis.
 */
export function decodeAndApplyServerMsg(bytes: Uint8Array): DecodeOutcome {
  const outcome: DecodeOutcome = { newResolutions: [], firstQuotes: [], quotesApplied: 0, unmappedContractIds: [] };
  let msg: ReturnType<typeof cqg.ServerMsg.decode>;
  try {
    msg = cqg.ServerMsg.decode(bytes);
  } catch (err) {
    if (import.meta.env.DEV) console.warn(ts(), '[cqg-decode] failed to decode ServerMsg; skipping', err);
    return outcome;
  }

  // 1) Learn contract metadata (contractId → symbol + scale) from EVERY source CQG sends it —
  // mirroring the legacy CqgSession.SetContractMetadataAsync, because the user's traded contracts
  // arrive via the historical-orders inline path, not symbol-resolution.
  const harvest = (cm: { contractId?: number | null; contractSymbol?: string | null; correctPriceScale?: number | null; tickSize?: number | null; tickValue?: number | null } | null | undefined) => {
    if (!cm || cm.contractId == null) return;
    const symbol = cm.contractSymbol ?? '';
    const scale = typeof cm.correctPriceScale === 'number' ? cm.correctPriceScale : 0;
    if (!symbol || !(scale > 0)) return; // can't key/unscale without both
    const contractId = cm.contractId;
    const had = contractsById.has(contractId);
    contractsById.set(contractId, {
      symbol,
      scale,
      tickSize: typeof cm.tickSize === 'number' && cm.tickSize > 0 ? cm.tickSize : undefined,
      tickValue: typeof cm.tickValue === 'number' && cm.tickValue > 0 ? cm.tickValue : undefined,
    });
    if (!had) {
      outcome.newResolutions.push(`${symbol}->#${contractId}`);
      resolutionListeners.forEach(l => { try { l(symbol, contractId); } catch { /* ignore */ } });
    }
  };

  const reports = msg.informationReports;
  if (reports && reports.length) {
    for (const r of reports) {
      // A FAILED request (status_code >= 100, e.g. 101 "You are not logged in" for a resolution sent
      // before the Logon was acked): tell the subscriber so it can re-ask instead of waiting forever
      // for a resolution that will never come (2026-09-13).
      if (r && typeof r.statusCode === 'number' && r.statusCode >= 100 && typeof r.id === 'number') {
        const failedId = r.id; const failedStatus = r.statusCode;
        resolutionFailureListeners.forEach(l => { try { l(failedId, failedStatus, r.textMessage ?? null); } catch { /* ignore */ } });
        continue;
      }
      harvest(r?.symbolResolutionReport?.contractMetadata);              // symbol resolution
      harvest(r?.contractMetadataReport?.contractMetadata);             // contract metadata report
      const hist = r?.historicalOrdersReport?.orderStatuses;            // historical orders (inline)
      if (hist) for (const os of hist) { const cms = os?.contractMetadata; if (cms) for (const cm of cms) harvest(cm); }
    }
  }
  // Live fills carry inline contract metadata too (ServerMsg.order_statuses[].contract_metadata).
  const liveOrders = msg.orderStatuses;
  if (liveOrders) for (const os of liveOrders) { const cms = os?.contractMetadata; if (cms) for (const cm of cms) harvest(cm); }

  // Human-readable FILL lines (dev/demo only): the raw frame log says "OrderStatuses bytes=347" —
  // this says what actually happened ("FILL BUY 2 F.US.MESU26 @7548.25 (order …)"). Fills are rare
  // (only when the user trades), so no throttle. fill_qty > 0 = a real fill (mirrors the BE builder).
  if (import.meta.env.MODE !== 'prod' && liveOrders) {
    const toNum = (d: { significand?: number | string | null; exponent?: number | null } | null | undefined): number => {
      if (!d || d.significand == null) return 0;
      const s = Number(d.significand);
      if (!Number.isFinite(s)) return 0;
      const e = d.exponent ?? 0;
      return e === 0 ? s : s * Math.pow(10, e);
    };
    for (const os of liveOrders) {
      const trxs = os?.transactionStatuses;
      if (!trxs || !trxs.length) continue;
      const side = os.order?.side === 2 ? 'SELL' : os.order?.side === 1 ? 'BUY' : '?';
      const symbol = (os.order?.contractId != null ? contractsById.get(os.order.contractId)?.symbol : undefined)
        ?? (os.order?.contractId != null ? `#${os.order.contractId}` : '?');
      for (const trx of trxs) {
        const qty = toNum(trx.fillQty);
        if (qty <= 0) continue;
        console.log('%s [cqg-decode] 🟢 FILL %s %s %s @ %s (order %s, trans %s)',
          ts(), side, qty, symbol, trx.correctFillPrice ?? '?', os.orderId ?? '?', String(trx.transId ?? '?'));
      }
    }
  }

  // 2) Apply real-time market data → liveQuotesStore.
  const rtmds = msg.realTimeMarketData;
  if (rtmds && rtmds.length) {
    for (const md of rtmds) {
      if (md.contractId == null) continue;
      const meta = contractsById.get(md.contractId);

      // Snapshot frames carry correct_price_scale (field 19); cache it onto the contract so later
      // (scale-less) update frames can still unscale. Updates leave it 0 → keep the cached one.
      if (meta && typeof md.correctPriceScale === 'number' && md.correctPriceScale > 0)
        meta.scale = md.correctPriceScale;

      // No symbol/scale yet (resolution report not seen) → defer, exactly like the BE drops
      // quotes for unknown contracts until metadata arrives.
      if (!meta || !(meta.scale > 0)) {
        if (!outcome.unmappedContractIds.includes(md.contractId)) outcome.unmappedContractIds.push(md.contractId);
        continue;
      }

      const quotes = md.quotes;
      if (!quotes || !quotes.length) continue;

      let last: number | null = null, bid: number | null = null, ask: number | null = null;
      for (const q of quotes) {
        if (q.scaledPrice == null) continue;
        const price = q.scaledPrice * meta.scale;
        switch (q.type) {
          case TYPE_TRADE:   last = price; break;
          case TYPE_BESTBID: bid  = price; break;
          case TYPE_BESTASK: ask  = price; break;
        }
      }
      if (last == null && bid == null && ask == null) continue;

      // liveQuotesStore.set merges with the previous quote (nulls don't clobber), so a tick that
      // only carries a new bid keeps the last trade price intact — matching the per-field updates.
      liveQuotesStore.set(meta.symbol, {
        last,
        bid,
        ask,
        ts: new Date().toISOString(),
      });
      outcome.quotesApplied++;
      if (!quotedOnce.has(meta.symbol)) { quotedOnce.add(meta.symbol); outcome.firstQuotes.push(meta.symbol); }
    }
  }

  return outcome;
}

/** Tick metadata for a resolved symbol, if the socket has surfaced its contract yet. */
export function getContractTickInfo(symbol: string): { tickSize?: number; tickValue?: number } | undefined {
  for (const meta of contractsById.values()) {
    if (meta.symbol === symbol) return { tickSize: meta.tickSize, tickValue: meta.tickValue };
  }
  return undefined;
}
